/**
 * View Component: Sidebar
 * Left rail with search, book filters, date filters, tag filters.
 *
 * T-Cron-2026-10-22 20:00 (round-4-of-day) — 改进清单 #10「无障碍」
 *
 * 修的问题:
 *   所有筛选 chip 都是 **toggle button**,选中态只靠 CSS class `.active`
 *   表达。读屏用户按下之后完全不知道当前是不是已经筛上了
 *   (读屏只会念按钮的静态文字)。⇒ 补 aria-pressed。
 *   另外 `<aside>` landmark 没有名字,页面上还有别的 complementary
 *   区域时读屏分不清 ⇒ 补 aria-label。
 *
 * T-Cron-2026-10-25 02:00 (round-1-of-day) — 改进清单 #7「拖拽排序」上生产线
 *
 * 本轮把「拖拽排序」搬进 Vite 生产树。背景:
 *   根目录老仓库(components/Sidebar.jsx,T-Cron-2026-10-10 08:00 / T77)
 *   早就实现了侧栏拖拽排序,但线上 canonical bundle 里
 *   `sidebar-item-drag-handle` / `aria-grabbed` / `xhs-kanban-book-order`
 *   全部 ABSENT ⇒ 这个功能**从来没上线过**,改进清单 #7 在生产是 0%。
 *
 * 本文件的做法:
 *   · 排序逻辑全部抽在 harness/useDraggableOrder.js(与老仓库 T77 同款契约);
 *   · 本文件只负责「把 chip 变成可拖的行 + 接上键盘重排」;
 *   · 只在 `(hover: hover) and (pointer: fine)` 设备启用(HTML5 DnD 在触屏
 *     上不会触发,与其留一个点不动的假 affordance,不如直接关);
 *   · 触屏/移动端行为**完全不变**(chip 列表的排版、点击、样式一个字节没动)。
 *
 * Props:
 *   filters: { search, book, date, tag, angle, source, onlyFavorites }
 *   bookList: [{ name, count }]
 *   dateList: [{ date, count }]
 *   tagList:  [{ name, count }]
 *   angleList: [{ name, count }]
 *   sourceList: [{ name, count }]
 *   onFilterChange: (key, value) => void
 *   onClear: () => void
 */
import { useDraggableOrder } from '../harness/useDraggableOrder.js';
import { buzz } from '../harness/haptics.js';
// T-Cron-2026-11-01 08:00 — 改进清单 #15「标签云」。
// 档位计算收口在 harness/tagCloud.js,桌面侧栏和移动端筛选抽屉共用同一套。
import { renderTagCloud } from '../harness/tagCloud.js';

// 每类列表一个独立的 localStorage key —— 调书籍顺序不该打乱热点的顺序。
const ORDER_KEYS = {
  batch: 'xhs-kanban-batch-order',
  book: 'xhs-kanban-book-order',
  hotspot: 'xhs-kanban-hotspot-order',
  source: 'xhs-kanban-source-order',
  angle: 'xhs-kanban-angle-order',
};

// 书籍列表里没有书名的条目显示「未分类」—— 与改动前的
// `name || '未分类'` 行为保持一致。
//
// ⚠️ 这个 fallback 必须**同时**用在排序 hook 的 normName 和 chip 的
// 渲染上:如果只有渲染侧兜底,hook 会把空名字的项 `.filter(Boolean)`
// 掉 —— 列表里就会**少一颗 chip**(比显示「未分类」更糟)。
const bookName = (item) => {
  const n = item && typeof item === 'object' ? item.name : item;
  return n || '未分类';
};

function SearchIcon() {
  return (
    <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
}

/**
 * 一组可拖拽重排的筛选 chip。
 *
 * 拖拽(pointer 设备):整颗 chip 是拖动目标,左侧的 ⋮⋮ 手柄只是 affordance 提示。
 * 键盘(所有设备):聚焦某颗 chip 后 Alt+↑ / Alt+↓ 上下移动它
 *   —— 拖拽不能是唯一的重排途径,否则纯键盘/读屏用户完全用不了这个功能。
 */
function DraggableChipList({
  items, activeValue, onSelect, dragHandlers, truncate = 0, countSuffix = '篇',
  chipClass = '', renderLabel = null, collapseAfter = 0,
}) {
  const {
    draggable, reorderable, draggedName, dragOverName,
    onDragStart, onDragOverName, onDragEnd, onDrop, onReorderKey,
  } = dragHandlers || {};
  const containerRef = React.useRef(null);

  // T-Cron-2026-10-26 02:00 — 改进清单 #13「性能」。
  //
  // 🔴 本轮实测发现的真实问题(不是理论优化):
  //   线上 canonical 的侧栏「按角度」渲染了 **1257 颗 chip** ——
  //   因为角度是自由文本("热点主导" / "热点主导100%" / "热点主导80%"
  //   / "热点+书" / "热点+书点睛" …),1388 个角度取值里只有 1253 个不同,
  //   而按「篇数」排序后头部 20 个也只覆盖 5% 的内容。
  //   ⇒ 侧栏 scrollHeight = **43,105px**(视口只有 513px)= 要滚 84 屏,
  //     全站 DOM 26,257 个元素 / 3,809 个 button,其中 1,257 颗是无用的角度 chip。
  //
  // 修法:超长列表默认只渲染前 N 项 + 一个「更多」按钮,**展开后全部可达**
  //      (不是丢弃,是折叠 —— 展开按钮保证任何一项都仍然能点到)。
  //
  // ⚠️ 与拖拽排序的关系:折叠只影响**渲染**,hook 里的顺序数组不变,
  //    展开后拖拽/Alt+↑↓ 重排对全部项依然有效。
  const [expanded, setExpanded] = React.useState(false);

  // 列表长度变化时收起 —— 否则展开状态会泄漏到别的分组。
  React.useEffect(() => {
    setExpanded(false);
  }, [items && items.length]);

  // 取名字的口径必须和渲染时**完全一致**(否则折叠区里的 active 判断会错)。
  const nameOf = React.useCallback(
    (item) => {
      const rawName = item && typeof item === 'object' ? item.name : item;
      return rawName || '未分类';
    },
    []
  );

  const collapsible = collapseAfter > 0 && items && items.length > collapseAfter;

  const visibleItems = React.useMemo(() => {
    if (!items || !items.length) return [];
    if (!collapsible || expanded) return items;
    const head = items.slice(0, collapseAfter);
    // 🔴 当前选中的项**必须**留在可见范围里 ——
    //    否则用户筛完之后,那一项消失在折叠区外,屏幕上完全看不到
    //    「我现在正在按哪个角度筛选」(筛选器仍在生效但没有视觉证据)。
    if (activeValue) {
      const hit = items.find((it) => nameOf(it) === activeValue);
      if (hit && !head.includes(hit)) head.push(hit);
    }
    return head;
  }, [items, collapsible, expanded, collapseAfter, activeValue, nameOf]);

  const handleKeyDown = (e) => {
    if (!reorderable) return;
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    const el = document.activeElement;
    if (!el || !containerRef.current || !containerRef.current.contains(el)) return;
    const name = el.dataset ? el.dataset.name : null;
    if (!name) return;
    // preventDefault:别让浏览器滚页面;stopPropagation:别让全局快捷键
    // 处理器也看到这次按键(useKeyboardShortcuts.js 本身已对 altKey 放行,
    // 这里双保险)。
    e.preventDefault();
    e.stopPropagation();
    onReorderKey(name, e.key === 'ArrowUp' ? -1 : 1);
    buzz(10);
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="chip-list" ref={containerRef} onKeyDown={handleKeyDown}>
      {visibleItems.map((item) => {
        const rawName = item && typeof item === 'object' ? item.name : item;
        const count = item && typeof item === 'object' ? (item.count ?? null) : null;
        // 书籍列表里没有书名的条目显示「未分类」—— 与改动前的
        // `name || '未分类'` 行为保持一致,不能因为搬进 DraggableChipList
        // 就变成一颗空白 chip。
        const name = rawName || '未分类';
        const isActive = activeValue === name;
        const isDragged = draggable && draggedName === name;
        const isDragOver = draggable && dragOverName === name && draggedName && draggedName !== name;
        const display = truncate && name && name.length > truncate ? name.slice(0, truncate) + '…' : name;
        return (
          <button
            key={name}
            className={`chip ${chipClass}${isActive ? ' active' : ''}${isDragged ? ' is-dragging' : ''}${isDragOver ? ' is-drag-over' : ''}`}
            data-name={name}
            onClick={() => {
              // T-Cron-2026-11-04 (T146) — 改进清单 #3「触感反馈」。
              // 普通筛选 chip 点击此前**没有**任何触感反馈:Sidebar 里的 buzz
              // 只覆盖了 4 条周边路径(键盘重排 / 拖拽起 / 拖放 / 「更多」展开),
              // 最常用的「点 chip 选中筛选」这条主路径反而是静默的。
              // 8ms 与 MobileFilterDrawer 的 filterTap、harness/tagCloud.js:156
              // 保持同一档位 ⇒ 全站「tap 确认」手感一致。
              // buzz() 内部守卫完备(不支持 vibrate 的设备静默跳过 + 尊重
              // prefers-reduced-motion)⇒ 有则震、无则静默,不影响筛选。
              buzz(8);
              onSelect(name);
            }}
            title={count != null ? `${name} (${count}${countSuffix})` : name}
            aria-pressed={activeValue === name}
            aria-label={count != null ? `${name}, ${count}${countSuffix}` : name}
            // T-Cron-2026-10-25:与老仓库 T77 保持 parity —— 拖拽中的行
            // 暴露 aria-grabbed,并把键盘手势写进 aria-keyshortcuts,
            // 让读屏用户 Tab 到这一行时就知道「这行能重排」。
            //
            // 这两个属性挂在 `reorderable`(全设备)而不是 `draggable`
            // (仅 fine pointer)上:读屏用户往往用读屏+键盘而不是鼠标,
            // 触屏设备上的读屏用户也必须知道这行可以重排。
            aria-grabbed={reorderable ? (isDragged ? 'true' : 'false') : undefined}
            aria-keyshortcuts={reorderable ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
            draggable={draggable ? true : undefined}
            onDragStart={draggable ? (e) => {
              // Firefox 老版本要求 setData 被调用过才允许启动拖拽。
              e.dataTransfer.setData('text/plain', name);
              e.dataTransfer.effectAllowed = 'move';
              onDragStart(name);
              buzz(8);
            } : undefined}
            onDragOver={draggable ? (e) => {
              // 必须 preventDefault,否则 drop 事件不会触发。
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragOverName !== name) onDragOverName(name);
            } : undefined}
            onDragLeave={draggable ? (e) => {
              // 只在指针真的离开这一颗 chip 时清高亮(relatedTarget 检查),
              // 否则在 chip 内部的 span 之间移动会闪烁。
              if (dragOverName === name && !e.currentTarget.contains(e.relatedTarget)) {
                onDragOverName(null);
              }
            } : undefined}
            onDrop={draggable ? (e) => {
              e.preventDefault();
              const from = e.dataTransfer.getData('text/plain');
              if (from && from !== name) {
                onDrop(from, name);
                buzz(12);
              }
            } : undefined}
            onDragEnd={draggable ? onDragEnd : undefined}
          >
            {draggable && (
              <span className="chip-drag-handle" aria-hidden="true" title="拖动重排">⋮⋮</span>
            )}
            <span className="chip-label">{renderLabel ? renderLabel(name) : display}</span>
            {count != null && (
              <span className="chip-count">{count}</span>
            )}
          </button>
        );
      })}
      {/* T-Cron-2026-10-26 02:00 — 改进清单 #13「性能」:折叠超长列表。
          折叠不是丢弃 —— 展开按钮保证任何一项都仍然可达。 */}
      {collapsible && (
        <button
          type="button"
          className="chip chip-more"
          onClick={() => { setExpanded((v) => !v); buzz(6); }}
          aria-expanded={expanded}
          aria-label={expanded
            ? `收起列表，共 ${items.length} 项`
            : `展开其余 ${items.length - visibleItems.length} 项，共 ${items.length} 项`}
        >
          {expanded ? '收起 ▲' : `更多 ${items.length - visibleItems.length} 项 ▼`}
        </button>
      )}
    </div>
  );
}

/**
 * 带排序提示的分组标题。
 *
 * 两种 affordance 按设备区分:
 *   · fine pointer(桌面)→ 「⋮⋮」= 拖拽手柄
 *   · 其它设备(触屏)  → 「⌨」= Alt+↑/↓ 键盘手势
 *
 * T-Cron-2026-10-25:键盘重排已从 pointer 闸门里解耦(见
 * useDraggableOrder 的 `reorderable`),所以**触屏上也要显示提示** ——
 * 触屏 + 外接键盘的用户同样需要知道这行能重排。之前的实现在触屏上
 * 把提示整个藏掉,等于对这类用户隐藏了唯一可用的重排路径。
 */
function SectionTitle({ children, sortHint, canDrag }) {
  const hintLabel = canDrag
    ? '可拖动 ⋮⋮ 手柄重排;聚焦后按 Alt+↑/↓ 也能移动'
    : '聚焦后按 Alt+↑/↓ 可上下移动这一项';
  return (
    <div className={`sidebar-section-title${sortHint ? ' has-sort-hint' : ''}`}>
      <span>{children}</span>
      {sortHint && (
        <span
          className={`sidebar-section-sort-hint${canDrag ? '' : ' is-keyboard-hint'}`}
          title={hintLabel}
          aria-label={hintLabel}
        >
          {canDrag ? '⋮⋮' : '⌨'}
        </span>
      )}
    </div>
  );
}

function Sidebar({ filters, bookList, dateList, hotspotList, tagList, angleList, sourceList, batchList, onFilterChange, onClear, searchInputRef, searchHistory, onCommitSearch, onRemoveSearchHistory, onClearSearchHistory }) {
  const hasActiveFilter = filters.search || filters.book || filters.date || filters.hotspot || filters.tag || filters.angle || filters.source || filters.batch || filters.onlyFavorites;

  // T-Cron-2026-10-24 08:00:搜索历史(改进清单 #15)。
  // 只在「输入框空着」时展示历史 —— 输入框有内容时用户正在打字,
  // 历史的候选词会挤占视觉空间,反而干扰。
  const showHistory = !!searchHistory && searchHistory.length > 0 && !filters.search;

  const commitSearch = (q) => {
    onFilterChange('search', q);
    if (onCommitSearch) onCommitSearch(q);
  };

  // T-Cron-2026-10-25 02:00:改进清单 #7「拖拽排序」。
  // 五个 hook 无条件调用(React 规则),hook 内部自己判断设备是否支持拖拽。
  // 标签列表**不**参与拖拽:标签极短,重排收益低而误拖成本高
  // (与老仓库 T77 的判断一致)。
  const batchDrag = useDraggableOrder(ORDER_KEYS.batch, batchList);
  const bookDrag = useDraggableOrder(ORDER_KEYS.book, bookList, bookName);
  const hotspotDrag = useDraggableOrder(ORDER_KEYS.hotspot, hotspotList);
  const sourceDrag = useDraggableOrder(ORDER_KEYS.source, sourceList);
  const angleDrag = useDraggableOrder(ORDER_KEYS.angle, angleList);

  // T-Cron-2026-11-01 08:00 — 改进清单 #15「标签云」。
  // 用 useMemo 缓存:tagList 是 useContentStore 里 memo 出来的稳定引用,
  // 但 filters.tag 每次点击都会变 ⇒ 这里必须把 filters.tag 一起列进依赖,
  // 否则选中态(高亮)不会跟着更新。
  const tagCloudItems = React.useMemo(
    () => renderTagCloud({
      tagList,
      activeTag: filters.tag,
      // 点击已选中的标签 = 取消筛选(与全站其它筛选芯片的语义一致)。
      onSelect: (name) => onFilterChange('tag', filters.tag === name ? '' : name),
      chipClass: 'chip',
    }),
    [tagList, filters.tag, onFilterChange]
  );

  return (
    <aside className="app-sidebar" aria-label="筛选侧栏">
      {/* Search */}
      <div className="search-box">
        <SearchIcon />
        <input
          type="text"
          placeholder="搜索标题 / 书名 / 标签 / 拼音首字母..."
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          onKeyDown={(e) => {
            // Enter = 提交这个查询 ⇒ 记进历史。
            if (e.key === 'Enter') {
              e.preventDefault();
              commitSearch(filters.search);
            }
          }}
          onBlur={(e) => {
            // 失焦也提交:用户点卡片/点别处 = 这个查询是认真的。
            // 重复提交无害(模块内去重)。
            //
            // ⚠️ 但要排除「点历史项」这种情况:点历史项会先触发 blur(焦点
            // 移到那个按钮),再触发 click。此时 blur 里提交的是**当前输入框
            // 里的旧文本**,会把「黑天」这种半截词也塞进历史 ⇒ 噪音。
            // ⇒ 用 relatedTarget 判断焦点是不是落进了历史面板。
            if (!filters.search) return;
            const next = e.relatedTarget;
            if (next && next.closest && next.closest('.search-history')) return;
            commitSearch(filters.search);
          }}
          ref={searchInputRef}
          aria-label="搜索选题，支持中文或拼音首字母，例如 sjxl 找《设计心理学》"
        />
        {showHistory && (
          <div className="search-history" role="group" aria-label="最近搜索">
            <div className="search-history-head">
              <span className="search-history-title">最近搜索</span>
              <button
                className="search-history-clear"
                onClick={onClearSearchHistory}
                aria-label="清空搜索历史"
                title="清空搜索历史"
              >
                清空
              </button>
            </div>
            <ul className="search-history-list">
              {searchHistory.map((q) => (
                <li key={q} className="search-history-item">
                  <button
                    className="search-history-query"
                    onClick={() => commitSearch(q)}
                    aria-label={`搜索 ${q}`}
                    title={q}
                  >
                    {q}
                  </button>
                  <button
                    className="search-history-remove"
                    onClick={() => onRemoveSearchHistory(q)}
                    aria-label={`删除搜索历史 ${q}`}
                    title="删除"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Favorite toggle */}
      <div className="sidebar-section">
        <div className="sidebar-section-title">选材状态</div>
        <div className="chip-list">
          <button
            className={`chip ${filters.onlyFavorites ? 'active' : ''}`}
            onClick={() => onFilterChange('onlyFavorites', !filters.onlyFavorites)}
            aria-pressed={!!filters.onlyFavorites}
          >
            ♥ 只看已选
          </button>
        </div>
      </div>

      {/* Batch — T-Cron-2026-10-25:可拖拽重排 */}
      {batchList && batchList.length > 0 && (
        <div className="sidebar-section">
          <SectionTitle sortHint={batchDrag.dragHandlers.sortHint} canDrag={batchDrag.dragHandlers.draggable}>按批次</SectionTitle>
          <DraggableChipList
            items={batchDrag.items}
            activeValue={filters.batch}
            onSelect={(name) => onFilterChange('batch', filters.batch === name ? '' : name)}
            dragHandlers={batchDrag.dragHandlers}
          />
        </div>
      )}

      {/* Books — T-Cron-2026-10-25:可拖拽重排。
          T-Cron-2026-10-26 02:00:110 本书折叠到 24 项(实测侧栏 1810px → 可控)。 */}
      <div className="sidebar-section">
        <SectionTitle sortHint={bookDrag.dragHandlers.sortHint} canDrag={bookDrag.dragHandlers.draggable}>按书籍</SectionTitle>
        <DraggableChipList
          items={bookDrag.items}
          activeValue={filters.book}
          onSelect={(name) => onFilterChange('book', filters.book === name ? '' : name)}
          dragHandlers={bookDrag.dragHandlers}
          chipClass="book-chip "
          collapseAfter={24}
        />
      </div>

      {/* Hotspots — T-Cron-2026-10-25:可拖拽重排。
          T-Cron-2026-10-26 02:00:158 个热点折叠到 24 项(实测 3840px → 可控)。 */}
      <div className="sidebar-section">
        <SectionTitle sortHint={hotspotDrag.dragHandlers.sortHint} canDrag={hotspotDrag.dragHandlers.draggable}>按热点</SectionTitle>
        <DraggableChipList
          items={hotspotDrag.items}
          activeValue={filters.hotspot}
          onSelect={(name) => onFilterChange('hotspot', filters.hotspot === name ? '' : name)}
          dragHandlers={hotspotDrag.dragHandlers}
          truncate={10}
          collapseAfter={24}
        />
      </div>

      {/* Source — T-Cron-2026-10-25:可拖拽重排 */}
      <div className="sidebar-section">
        <SectionTitle sortHint={sourceDrag.dragHandlers.sortHint} canDrag={sourceDrag.dragHandlers.draggable}>按来源</SectionTitle>
        <DraggableChipList
          items={sourceDrag.items}
          activeValue={filters.source}
          onSelect={(name) => onFilterChange('source', filters.source === name ? '' : name)}
          dragHandlers={sourceDrag.dragHandlers}
          chipClass="source-chip "
          renderLabel={(name) => (name === 'txt' ? '新版 TXT' : '旧版 MD')}
        />
      </div>

      {/* Angle — T-Cron-2026-10-25:可拖拽重排。
          T-Cron-2026-10-26 02:00 改进清单 #13「性能」:
          🔴 本轮实测的**最严重性能问题** —— 角度是自由文本字段,
          线上渲染了 **1257 颗 chip**,侧栏 scrollHeight 达到 **43,105px**
          (视口 513px ⇒ 要滚 84 屏),全站 DOM 26,257 个元素 / 3,809 个 button。
          1388 个角度取值里只有 1253 个不同(还有"热点主导100%"这种带百分号的
          近重复),按篇数排序后头部 20 项也只覆盖 5% 的内容 ⇒ 高成本零收益。
          ⇒ 折叠到 24 项 + 「更多」按钮(展开后 1257 项全部可达、可拖拽重排)。 */}
      <div className="sidebar-section">
        <SectionTitle sortHint={angleDrag.dragHandlers.sortHint} canDrag={angleDrag.dragHandlers.draggable}>按角度</SectionTitle>
        <DraggableChipList
          items={angleDrag.items}
          activeValue={filters.angle}
          onSelect={(name) => onFilterChange('angle', filters.angle === name ? '' : name)}
          dragHandlers={angleDrag.dragHandlers}
          chipClass="angle-chip "
          collapseAfter={24}
        />
      </div>

      {/* Dates */}
      <div className="sidebar-section">
        <div className="sidebar-section-title">按日期</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dateList.map(({ date, count }) => (
            <button
              key={date}
              className={`date-item ${filters.date === date ? 'active' : ''}`}
              onClick={() => onFilterChange('date', filters.date === date ? '' : date)}
              aria-pressed={filters.date === date}
            >
              <span>{ContentService.formatDate(date)}</span>
              <span className="date-item-count">{count}篇</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tags — 刻意**不**参与拖拽(标签极短,重排收益低 / 误拖成本高)
          T-Cron-2026-11-01 08:00 早间 — 改进清单 #15「标签云」。
          标签分布极端长尾(526 … 39,差 13.5 倍)⇒ 用**对数刻度**分 3 档,
          字号/字重体现相对热度。详见 harness/tagCloud.js 的实测数据说明。 */}
      <div className="sidebar-section">
        <div className="sidebar-section-title">热门标签</div>
        <div className="chip-list tag-cloud">
          {tagCloudItems.map((t) => (
            <button
              key={t.key}
              className={t.className}
              onClick={t.onClick}
              title={`${t.name} (${t.count}篇)`}
              aria-pressed={t.isActive}
              aria-label={t.ariaLabel}
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* Clear filters */}
      {hasActiveFilter && (
        <button className="clear-filters" onClick={onClear} aria-label="清除全部筛选条件">
          清除全部筛选
        </button>
      )}
    </aside>
  );
}

window.Sidebar = Sidebar;

export default Sidebar;
